import React, { useEffect, useRef, useState } from 'react';
import apiService from '../services/ApiService';

type EntityPhotoUploadProps = {
  label: string;
  helpText?: string;
  currentUrl?: string | null;
  /** Circle for club/user-style avatars; banner for event flyers. */
  shape?: 'circle' | 'banner';
  maxSizeMb?: number;
  disabled?: boolean;
  onUpload: (file: File) => Promise<string>;
  onDelete?: () => Promise<void>;
  onUpdated?: (url: string | null) => void;
  onError?: (message: string) => void;
};

/**
 * Single-image upload for clubs (profile) and events (flyer).
 * Matches avatar UX without crop/position controls.
 */
export const EntityPhotoUpload: React.FC<EntityPhotoUploadProps> = ({
  label,
  helpText,
  currentUrl = null,
  shape = 'circle',
  maxSizeMb = 5,
  disabled = false,
  onUpload,
  onDelete,
  onUpdated,
  onError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(currentUrl);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setPreview(currentUrl);
  }, [currentUrl]);

  const reportError = (msg: string) => {
    setLocalError(msg);
    onError?.(msg);
  };

  const displaySrc =
    preview && preview.startsWith('blob:')
      ? preview
      : preview
        ? apiService.getFileUrl(preview) || preview
        : null;

  const validate = (file: File): boolean => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      reportError('Please upload a JPEG, PNG, or WebP image');
      return false;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      reportError(`File size must be less than ${maxSizeMb}MB`);
      return false;
    }
    return true;
  };

  const handleFile = async (file: File | undefined) => {
    if (!file || disabled || busy) return;
    if (!validate(file)) return;
    setLocalError(null);
    const blobUrl = URL.createObjectURL(file);
    setPreview(blobUrl);
    setBusy(true);
    try {
      const path = await onUpload(file);
      URL.revokeObjectURL(blobUrl);
      setPreview(path);
      onUpdated?.(path);
    } catch (e: unknown) {
      URL.revokeObjectURL(blobUrl);
      setPreview(currentUrl);
      reportError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!onDelete || disabled || busy) return;
    setBusy(true);
    setLocalError(null);
    try {
      await onDelete();
      setPreview(null);
      onUpdated?.(null);
    } catch (e: unknown) {
      reportError(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  const frameClass =
    shape === 'banner'
      ? 'w-full max-w-md aspect-[16/10] rounded-xl'
      : 'w-24 h-24 rounded-full';

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-medium text-white">{label}</h3>
        {helpText ? <p className="text-meta mt-1">{helpText}</p> : null}
      </div>

      <div className={`flex ${shape === 'banner' ? 'flex-col' : 'flex-row items-center'} gap-4`}>
        <div
          className={`${frameClass} overflow-hidden border border-white/[0.08] bg-[#0a0a0a] shrink-0`}
        >
          {displaySrc ? (
            <img src={displaySrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-meta text-xs">
              No photo
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={disabled || busy}
            className="btn-secondary btn-sm cursor-pointer"
            onClick={() => inputRef.current?.click()}
          >
            {busy ? 'Working…' : preview ? 'Change photo' : 'Upload photo'}
          </button>
          {preview && onDelete ? (
            <button
              type="button"
              disabled={disabled || busy}
              className="btn-ghost btn-sm cursor-pointer"
              onClick={handleDelete}
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        className="hidden"
        disabled={disabled || busy}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {localError ? (
        <p className="text-sm text-red-300" role="alert">
          {localError}
        </p>
      ) : null}
    </div>
  );
};

export default EntityPhotoUpload;
